import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const AuthContext = createContext();

export const useUserContext = () => useContext(AuthContext);

const REAUTH_ERRORS = new Set([
    'missing_refresh_token',
    'invalid_grant',
    'login_required',
    'mfa_required',
    'interaction_required',
    'consent_required',
]);

const isReauthError = (e) => {
    if (!e) return false;
    if (REAUTH_ERRORS.has(e.error)) return true;
    const desc = e.error_description || e.message || '';
    return /refresh token/i.test(desc);
};

// Module-level guard against concurrent bounces. Reset by both successful
// navigation (page reload wipes the module) and our explicit catch path so
// the app can recover if loginWithRedirect itself fails.
let bouncing = false;

export const UserContextProvider = ({ children }) => {
    const { getAccessTokenSilently, isAuthenticated, user, loginWithRedirect, isLoading, logout } = useAuth0();
    const [token, setToken] = useState(null);
    // Keep the latest Auth0 fns in a ref so the memoized bouncer always
    // closes over current versions without recreating the function each render.
    const auth0Ref = useRef({ logout, loginWithRedirect });
    auth0Ref.current = { logout, loginWithRedirect };

    const bounceToAuth0 = useCallback(async (err) => {
        if (bouncing) return;
        bouncing = true;
        const returnTo = window.location.pathname + window.location.search;
        console.log('Bouncing to Auth0 for re-auth:', err?.error || err?.message || 'unknown', '->', returnTo);
        try {
            try {
                await auth0Ref.current.logout({ openUrl: false });
            } catch (logoutErr) {
                console.warn('logout({openUrl:false}) failed; clearing local Auth0 cache manually', logoutErr);
                try {
                    for (let i = localStorage.length - 1; i >= 0; i--) {
                        const key = localStorage.key(i);
                        if (key && key.startsWith('@@auth0spajs@@')) {
                            localStorage.removeItem(key);
                        }
                    }
                } catch (_) { /* ignore */ }
            }
            await auth0Ref.current.loginWithRedirect({ appState: { returnTo } });
        } catch (redirectErr) {
            // loginWithRedirect failed before navigating away. Reset the guard
            // so the user can retry instead of getting permanently stuck.
            bouncing = false;
            console.error('bounceToAuth0: loginWithRedirect failed; bounce guard reset', redirectErr);
            throw redirectErr;
        }
    }, []);

    useEffect(() => {
        const getToken = async () => {
            try {
                const accessToken = await getAccessTokenSilently();
                setToken(accessToken);
            } catch (e) {
                if (isReauthError(e)) {
                    await bounceToAuth0(e);
                    return;
                }
                console.error(e);
            }
        };

        if (isAuthenticated && !isLoading) {
            getToken();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect, isLoading, logout]);

    const userId = user?.sub?.split('|')[1];

    useEffect(() => {
        axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;

        const requestInterceptorId = axios.interceptors.request.use(async (config) => {
            if (!config.baseURL) {
                return config;
            }
            try {
                const accessToken = await getAccessTokenSilently();
                config.headers['Authorization'] = `Bearer ${accessToken}`;
                return config;
            } catch (e) {
                if (isReauthError(e)) {
                    await bounceToAuth0(e);
                }
                return Promise.reject(e);
            }
        });

        const responseInterceptorId = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const original = error?.config;
                if (!original || original.__retriedFor401) {
                    return Promise.reject(error);
                }
                if (error?.response?.status === 401) {
                    original.__retriedFor401 = true;
                    try {
                        const fresh = await getAccessTokenSilently({ cacheMode: 'off' });
                        original.headers = original.headers || {};
                        original.headers['Authorization'] = `Bearer ${fresh}`;
                        return axios(original);
                    } catch (refreshErr) {
                        if (isReauthError(refreshErr)) {
                            await bounceToAuth0(refreshErr);
                        }
                        return Promise.reject(error);
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.request.eject(requestInterceptorId);
            axios.interceptors.response.eject(responseInterceptorId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAccessTokenSilently, logout, loginWithRedirect]);

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, token, userId }}>
            {children}
        </AuthContext.Provider>
    );
};

export const withUserContext = (
    Component
) => {
    return function WithUser(props) {
        return (
            <AuthContext.Consumer>
                {(userContext) => (
                    <Component {...props} {...userContext} />
                )}
            </AuthContext.Consumer>
        );
    };
};
